import React, { FC, Key, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import { Guide, GuideUserStatus } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";

import Table from "@app/component/Table";
import AssignGuidesModal from "@app/scene/onboarding/components/OnboardingUsers/AssignGuidesModal";
import { updateUserGuides } from "@app/scene/onboarding/store/actions";
import UserWithStatus from "@app/scene/onboarding/types/UserWithStatus";

import useColumns from "./useColumns";
import styles from "./Users.module.scss";

const messages = defineMessages({
  assignModalSubTitle: {
    id: "onboarding.users.table.assignGuideModal.subTitle",
    defaultMessage: "Select teams and users below to assign to the guide",
    description: "Subtitle of the Assign Users modal on the User Guides page",
  },
});

export interface UsersProps {
  loading?: boolean;
  onSelectionChange?: (selectedUserIds: User["id"][]) => void;
  selectedUserIds: User["id"][];
  users: User[];
  usersStatus: Record<User["id"], GuideUserStatus[]>;
}

interface Props extends UsersProps {
  onUpdateUserGuides: typeof updateUserGuides.request;
}

const Users: FC<Props> = ({
  loading,
  onSelectionChange,
  onUpdateUserGuides,
  selectedUserIds,
  users,
  usersStatus,
}) => {
  const intl = useIntl();

  const [assignModalUser, setAssignModalUser] = useState<undefined | User>();
  const hideAssignModal = useCallback(() => setAssignModalUser(undefined), []);

  const handleChangeGuides = useCallback(
    (guideIds: Guide["id"][]) => {
      if (!assignModalUser) {
        return;
      }

      onUpdateUserGuides({ guideIds, userId: assignModalUser.id });
      setAssignModalUser(undefined);
    },
    [onUpdateUserGuides, assignModalUser]
  );

  const columns = useColumns(intl, setAssignModalUser);

  const handleSelectionChange = useCallback(
    (keys: Key[]) => onSelectionChange?.(keys as User["id"][]),
    [onSelectionChange]
  );

  const rows = useMemo(() => {
    return users.map((user) => ({ ...user, statuses: usersStatus[user.id] ?? [] }));
  }, [users, usersStatus]);

  return (
    <>
      <Table<UserWithStatus>
        className={styles.container}
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        rowKey="id"
        rowSelection={{
          onChange: handleSelectionChange,
          selectedRowKeys: selectedUserIds,
        }}
        scrollable
        showBorder
      />
      {assignModalUser && (
        <AssignGuidesModal
          allowEmpty
          onChange={handleChangeGuides}
          onHide={hideAssignModal}
          subTitle={intl.formatMessage(messages.assignModalSubTitle)}
          value={(usersStatus[assignModalUser.id] ?? []).map(({ guideId }) => guideId)}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  onUpdateUserGuides: updateUserGuides.request,
};

export default connect(null, mapDispatchToProps)(Users);
